import Button from 'Components/Base/Button'
import Checkbox from 'Components/Base/Checkbox'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { setLoading } from 'reducers/tempSlice'
import styled, { useTheme } from 'styled-components'
import { IntakeAllergy } from 'types/Medication'
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks'
import usePatientIntake, { IntakeAllergyState, IntakeHistoryState, IntakeHistoryType, PrimaryPhysicianState } from '../../../hooks/usePatientIntake'
import { getAccessToken } from '../../../reducers/authSlice'
import Progress from '../components/Progress'
import Allergies from './Allergies/Allergies'
import MultiSelectDropdown from './MultiSelect/MultiSelect'
import PhysicianCard from './PhysicianCard'
import PhysicianSelectCard from './PhysicianSelectCard'
import './PhysiciansForm.scss'

type Option = { selected: boolean; option: string }
type Allergy = { medication: string; reaction: string[]; allergic: boolean }

interface Props {
  backPressed: () => void
  onSubmit: () => void
}
export default function MedicalBasics({ onSubmit, backPressed }: Props) {
  const [openForm, setOpenForm] = useState<boolean>(false)
  const [openAllergies, setOpenAllergies] = useState<boolean>(false)
  const [noAllergies, setNoAllergies] = useState<boolean>(false)
  const [physicians, setPhysicians] = useState<PrimaryPhysicianState[]>([])
  const [allergies, setAllergies] = useState<Allergy[]>([])
  const [historyOther, setHistoryOther] = useState<string>('')
  const [surgeryOther, setSurgeryOther] = useState<string>('')
  const [selectedHistory, setSelectedHistory] = useState<Option[]>([])
  const historyList: Option[] = [
    { selected: false, option: 'Blood clots' },
    { selected: false, option: 'Depression' },
    { selected: false, option: 'Irregular heartbeat' },
    { selected: false, option: 'Kidney failure/dialysis' },
    { selected: false, option: 'Stroke' },
    { selected: false, option: 'Cancer' },
    { selected: false, option: 'Heart attack' },
    { selected: false, option: 'Kidney or gall stones' },
    { selected: false, option: 'Liver failure' },
    { selected: false, option: 'Ulcer' },
  ]
  const [selectedSurgery, setSelectedSurgery] = useState<Option[]>([])
  const surgeryList: Option[] = [
    { selected: false, option: 'Angioplasty/Bypass' },
    { selected: false, option: 'Gallbladder removed' },
    { selected: false, option: 'Hysterectomy' },
    { selected: false, option: 'Organ transplant' },
    { selected: false, option: 'Spleen removed' },
    { selected: false, option: 'Appendix removed' },
    { selected: false, option: 'Hernia repair' },
    { selected: false, option: 'Joint replacement' },
    { selected: false, option: 'Pacemaker/Defibrilator' },
    { selected: false, option: 'Thyroid removed' },
  ]

  const [primaryPhysician, setPrimaryPhysician] = useState<PrimaryPhysicianState>({
    hasPrimaryPhysician: false,
    firstName: '',
    lastName: '',
    city: '',
    state: '',
    quit: false,
  })
  const dispatch = useAppDispatch()

  const accessToken = useAppSelector(getAccessToken)
  const { getPhysicianInfo, postHistoryInfo, postAllergyInfo, getAllergyInfo, postPhysicianInfo } = usePatientIntake({ token: accessToken })
  const { getHistoryInfo } = usePatientIntake({
    token: accessToken,
  })
  const theme = useTheme()
  useEffect(() => {
    dispatch(setLoading(true))
    async function getPhysicianState() {
      const response = await getPhysicianInfo()
      const data = await response.data

      if (data.primaryPhysician) {
        const physs = data.physicians
        physs.unshift(data.primaryPhysician)

        setPhysicians(physs)
        setPrimaryPhysician(data.primaryPhysician)
      } else {
        setPhysicians(data.physicians)
      }
    }
    getPhysicianState().then(() => dispatch(setLoading(false)))
  }, [])

  useEffect(() => {
    dispatch(setLoading(true))
    async function getHistoryState() {
      const response = await getHistoryInfo()
      const data = await response.data
      setSelectedHistory(data.medical.historyOptions)
      setSelectedSurgery(data.surgical.historyOptions)
    }
    getHistoryState().then(() => dispatch(setLoading(false)))
  }, [])

  useEffect(() => {
    dispatch(setLoading(true))
    async function getAllergies() {
      const response = await getAllergyInfo()
      const data = await response.data
      const mappedAllergies: Allergy[] = []

      data.allergies.map((x) =>
        mappedAllergies.push({
          allergic: x.allergic,
          medication: x.medication,
          reaction: x.reaction.split(', '),
        }),
      )
      mappedAllergies.filter((a) => a.allergic).length > 0 && setOpenAllergies(true)
      setAllergies(mappedAllergies.filter((a) => a.allergic))
    }
    getAllergies().then(() => dispatch(setLoading(false)))
  }, [])

  const initialValues: PrimaryPhysicianState = {
    ...primaryPhysician,
  }

  const submitMedBasics = () => {
    postPhysicianInfo(primaryPhysician)

    const mappedAllergies: IntakeAllergy[] = []
    allergies.map((x) =>
      mappedAllergies.push({
        allergic: x.allergic,
        medication: x.medication,
        reaction: x.reaction.join(', '),
      }),
    )
    const allergyInfo: IntakeAllergyState = {
      allergies: mappedAllergies,
    }

    postAllergyInfo(allergyInfo)

    const values: IntakeHistoryState = {
      MedicalHistory: {
        HistoryType: IntakeHistoryType.Medical,
        HistoryOptions: selectedHistory,
        Notes: '',
      },
      SurgicalHistory: {
        HistoryType: IntakeHistoryType.Surgical,
        HistoryOptions: selectedSurgery,
        Notes: '',
      },
    }

    postHistoryInfo(values).then(() => onSubmit())
  }

  const formik = useFormik({
    initialValues,
    //validationSchema,
    onSubmit: submitMedBasics,
  })

  const removeAllergy = (item) => {
    setAllergies(allergies.filter((a) => a !== item))
  }

  const addHistory = () => {
    setSelectedHistory((history) => [...history, { selected: true, option: historyOther }])
    setHistoryOther('')
  }

  const addSurgery = () => {
    setSelectedSurgery((history) => [...history, { selected: true, option: surgeryOther }])
    setSurgeryOther('')
  }

  const toggleHistoryOption = (option: Option) => {
    const containsOption = selectedHistory.find((item) => item.option === option.option)
    if (containsOption) {
      const historyNew = selectedHistory.map((item) => (item.option === option.option ? { ...item, selected: !item.selected } : item))
      setSelectedHistory(historyNew)
    } else {
      option.selected = true
      setSelectedHistory((history) => [...history, option])
    }
  }

  const toggleSurgeryOption = (option: Option) => {
    const containsOption = selectedSurgery.find((item) => item.option === option.option)
    if (containsOption) {
      const surgeryNew = selectedSurgery.map((item) => (item.option === option.option ? { ...item, selected: !item.selected } : item))
      setSelectedSurgery(surgeryNew)
    } else {
      option.selected = true
      setSelectedSurgery((history) => [...history, option])
    }
  }

  const removePhys = (phys: PrimaryPhysicianState) => {
    const meds = physicians.filter((m) => phys !== m)
    setPhysicians(meds)
  }

  return (
    <Container>
      <Card>
        <Progress current={1} total={3} />
        <Header>Let’s Start with the Basics</Header>
        <Para>Answering these questions helps us understand your health and take better care of you. Let’s get started!</Para>
        {physicians.length > 0 && (
          <div style={{ marginBottom: '10px', position: 'relative' }}>
            <Typography
              styles={{
                fontWeight: 'bold',
                textAlign: 'left',
                marginTop: '20px',
                marginBottom: '20px',
              }}
              size={20}
            >
              Doctors
            </Typography>
            <Typography
              styles={{
                marginBottom: '20px',
              }}
            >
              Select your primary care doctor. Don’t see them? Just add them below.
            </Typography>
            {physicians.map((phys, index) => (
              <PhysicianSelectCard
                key={index}
                phys={phys}
                primaryPhysician={primaryPhysician}
                handleSetPrimaryPhysician={(phys) => setPrimaryPhysician(phys)}
                handleRemovePhys={(phys) => removePhys(phys)}
              />
            ))}
          </div>
        )}

        {!openForm && (
          <Button
            text="Add"
            leftIcon="circle-plus"
            backgroundColor={theme.colors.status.good}
            styles={{ marginBottom: 30 }}
            onClick={() => setOpenForm(!openForm)}
          />
        )}
        {openForm && (
          <>
            <PhysicianCard
              handleSetPrimaryPhysician={(phys) => {
                setPrimaryPhysician(phys)
                setPhysicians([...physicians, phys])
                setOpenForm(!openForm)
              }}
              props={formik}
              cancelInput={() => {
                setOpenForm(false)
              }}
            />
          </>
        )}
        <Typography size={20}>
          <b>Allergies</b>
        </Typography>
        <TagList>
          {allergies.map((a: Allergy, i) => (
            <Tag key={i}>
              {a.medication} -
              {a.reaction.map((symptom: string, i) => (
                <Typography key={i}>
                  {symptom}
                  {i === a.reaction.length - 1 ? '' : ','}
                </Typography>
              ))}
              <CloseBtn onClick={() => removeAllergy(a)}>✕</CloseBtn>
            </Tag>
          ))}
        </TagList>
        {!openAllergies && (
          <Row styles={{ width: 400, marginTop: 10 }}>
            {!noAllergies && (
              <Button
                text="Add"
                leftIcon="circle-plus"
                backgroundColor={theme.colors.status.good}
                styles={{ marginBottom: 30 }}
                onClick={() => setOpenAllergies(!openAllergies)}
              />
            )}
            {allergies.length === 0 && (
              <NoAll>
                <Checkbox checked={noAllergies} styles={{ marginRight: 5 }} onClick={() => setNoAllergies(!noAllergies)}></Checkbox>
                <Typography>I have no allergies</Typography>
              </NoAll>
            )}
          </Row>
        )}
        {openAllergies && (
          <Allergies
            allergies={allergies}
            handleSetAllergies={(allergies: Allergy[]) => setAllergies(allergies)}
            handleSetOpenAllergies={() => setOpenAllergies(!openAllergies)}
          />
        )}
        <Typography size={20} styles={{ marginBottom: 20 }}>
          <b>Medical History</b>
        </Typography>
        <Para>Do you have, or have you ever had, any of these health problems? (Select all that apply)</Para>
        <MultiSelectDropdown options={historyList} selected={selectedHistory} handleSetOptions={(option: Option) => toggleHistoryOption(option)} />
        <Para>Don't see the health problem in the list? Enter here</Para>
        <SearchWrapper>
          <SearchInput placeholder="Enter other health problem" value={historyOther} onChange={(e) => setHistoryOther(e.target.value)} />
          <Button text="Add" onClick={addHistory} />
        </SearchWrapper>
        <Para>Have you ever had any of these surgeries? (Select all that apply)</Para>
        <MultiSelectDropdown options={surgeryList} selected={selectedSurgery} handleSetOptions={(option: Option) => toggleSurgeryOption(option)} />
        <Para>Don't see the surgery in the list? Enter here</Para>
        <SearchWrapper>
          <SearchInput placeholder="Enter other surgery" value={surgeryOther} onChange={(e) => setSurgeryOther(e.target.value)} />
          <Button text="Add" onClick={addSurgery} />
        </SearchWrapper>
        <Button text="Continue" styles={{ marginBottom: 30 }} width={300} onClick={formik.handleSubmit} />
      </Card>
      <Image src="https://triacentral.blob.core.windows.net/dev-storage/portal/enrollment/Care%20Team%201.png" />
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  justify-content: space-between;
  height: fit-content;
  padding-top: 0;
  @media only screen and (max-width: 600px) {
    margin-top: 0px;
  }
`

const Card = styled.div`
  font-family: Raleway;
  max-width: 600px;
  height: fit-content;
  padding: 40px;
  text-align: left;
  @media only screen and (max-width: 600px) {
    margin-top: 0px;
    padding: 0px 20px 40px;
    width: 100%;
  }
`

const Header = styled.h1`
  font-weight: 700;
  font-size: 36px;
  margin-bottom: 20px;
`

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`

const Tag = styled.div`
  background: #007b8f;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const CloseBtn = styled.span`
  cursor: pointer;
`

const Para = styled.p`
  font-size: 16px;
  margin-bottom: 5px;
`
const NoAll = styled.div`
  padding: 10px 15px;
  border: 1px solid #bdbdbd;
  display: flex;
  width: fit-content;
  border-radius: 5px;
  margin-left: 10px;
  height: 45px;
`
const Image = styled.img`
  max-height: 530px;
  padding: 40px;
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`
const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: #e6f6f8;
  border: 1px solid #9bd3dd;
  border-radius: 4px;
  padding: 6px 8px;
  margin-top: 15px;
  margin-bottom: 15px;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background-color: #e6f6f8;
`
